import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import clinicService from '../../services/clinic.service';
import '../reputation/reputation.css';
import './welcome.css';
import './survey.css';

const DEFAULT_THANK_YOU = 'Thank you for completing this survey.';
const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']);
const THEME_ICON_COLORS = new Set([
  'primary',
  'secondary',
  'success',
  'warning',
  'info',
  'danger',
  'dark',
  'muted',
]);

const normalizeUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  if (!trimmed) return null;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

const isAutoValue = (value) => {
  if (value == null) return true;
  const trimmed = String(value).trim();
  return !trimmed || trimmed.toLowerCase() === 'auto';
};

const normalizeAlign = (value) => {
  const raw = String(value || 'center').trim().toLowerCase();
  if (raw === 'end' || raw === 'right') return 'right';
  if (raw === 'start' || raw === 'left') return 'left';
  if (raw === 'justify') return 'justify';
  if (raw === 'center') return 'center';
  return 'center';
};

const resolveHeadingType = (value) => {
  const tag = String(value || 'h1').trim().toLowerCase();
  return HEADING_TAGS.has(tag) ? tag : 'h1';
};

const buildPageStyle = (survey) => {
  if (!survey) return undefined;
  const pageWidth = isAutoValue(survey.pageWidth) ? '900px' : String(survey.pageWidth).trim();
  const style = {
    width: '100%',
    maxWidth: pageWidth,
    margin: '0 auto',
    boxSizing: 'border-box',
  };
  if (!isAutoValue(survey.pagePaddingDesktop)) {
    style.padding = survey.pagePaddingDesktop;
  }
  return style;
};

const buildContainerStyle = (survey) => {
  if (!survey) return undefined;
  const style = {};
  if (!isAutoValue(survey.formContainerPadding)) {
    style.padding = survey.formContainerPadding;
  }
  if (!isAutoValue(survey.formContainerBackgroundColor)) {
    style.backgroundColor = survey.formContainerBackgroundColor;
  }
  if (!isAutoValue(survey.formContainerBorderRadius)) {
    style.borderRadius = survey.formContainerBorderRadius;
  }
  if (survey.formContainerShowCard) {
    style.border = '1px solid var(--rep-line, #e1e5e9)';
    if (survey.formContainerCardShadow) {
      style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
    }
  }
  return style;
};

const extractOptions = (question) => {
  const rawOptions =
    (Array.isArray(question?.options) && question.options.length > 0
      ? question.options
      : null) ||
    question?.questionOption?.choices ||
    question?.questionOption?.options ||
    (Array.isArray(question?.questionOption) ? question.questionOption : null);

  if (!Array.isArray(rawOptions) || rawOptions.length === 0) {
    return [];
  }

  return rawOptions.map((opt, index) => {
    if (typeof opt === 'string') {
      return { id: `${index}`, text: opt, value: opt };
    }
    return {
      id: opt.id || `${index}`,
      text: opt.text || opt.label || opt.value || `Option ${index + 1}`,
      value: opt.value ?? opt.text ?? opt.label ?? `${index}`,
    };
  });
};

const normalizeQuestions = (questions = []) =>
  [...questions]
    .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0))
    .map((question) => ({
      ...question,
      required: Boolean(question.isRequired ?? question.required),
      options: extractOptions(question),
      minValue: Number(question.minValue) > 0 ? Number(question.minValue) : 1,
      maxValue:
        Number(question.questionOption?.max) >= Number(question.minValue || 1)
          ? Number(question.questionOption.max)
          : Number(question.maxValue) >= Number(question.minValue || 1)
            ? Number(question.maxValue)
            : 5,
      minLabel: question.questionOption?.minLabel || question.minLabel || 'Poor',
      maxLabel: question.questionOption?.maxLabel || question.maxLabel || 'Excellent',
      ratingType: String(
        question.ratingType || question.questionOption?.ratingType || 'stars'
      )
        .trim()
        .toLowerCase(),
    }));

const serializeAnswerValue = (value) => {
  if (value === undefined || value === null) {
    return '';
  }
  if (Array.isArray(value)) {
    return JSON.stringify(value);
  }
  return String(value);
};

function SurveyIcon({ survey, align }) {
  if (!survey?.icon) return null;
  const color = String(survey.iconColor || 'primary').trim();
  const themeClass = THEME_ICON_COLORS.has(color.toLowerCase())
    ? `text-${color.toLowerCase()}`
    : '';
  const style = THEME_ICON_COLORS.has(color.toLowerCase()) ? undefined : { color };

  return (
    <div className={`patient-survey__icon text-${align}`} aria-hidden="true">
      <i className={`bi bi-${survey.icon} ${themeClass}`.trim()} style={style} />
    </div>
  );
}

function SurveyHeader({ survey, patientFirstName }) {
  if (!survey) return null;

  const headingTag = resolveHeadingType(survey.headingType);
  const titleAlign = normalizeAlign(survey.headerPosition);
  const descriptionAlign = normalizeAlign(survey.descriptionPosition);
  const descriptionColor = survey.descriptionColor?.trim() || undefined;

  return (
    <header className="patient-survey__header">
      <SurveyIcon survey={survey} align={titleAlign} />
      {survey.title
        ? React.createElement(
            headingTag,
            {
              className: `patient-survey__title text-${titleAlign}`,
            },
            survey.title
          )
        : null}
      {survey.description ? (
        <p
          className={`patient-survey__description text-${descriptionAlign}`}
          style={descriptionColor ? { color: descriptionColor } : undefined}
        >
          {survey.description}
        </p>
      ) : null}
      {patientFirstName ? (
        <p className={`patient-survey__greeting text-${titleAlign}`}>
          Hi {patientFirstName}, please share your feedback below.
        </p>
      ) : null}
    </header>
  );
}

function SurveyProgress({ survey, answeredCount, total, currentPage, singleQuestionPerPage }) {
  if (!survey?.showProgressbar || !total) return null;

  const isSteps = String(survey.progressbarType || '').toUpperCase() === 'STEPS';
  let percent;
  let label;

  if (singleQuestionPerPage) {
    const current = Math.min(currentPage + 1, total);
    percent = Math.round((current / total) * 100);
    label = isSteps ? `Question ${current} of ${total}` : `${percent}% complete`;
  } else {
    percent = Math.round((answeredCount / total) * 100);
    label = isSteps ? `${answeredCount} of ${total} answered` : `${percent}% complete`;
  }

  return (
    <div className="patient-survey__progress">
      <div className="patient-survey__progress-meta">
        <span>Progress</span>
        <span>{label}</span>
      </div>
      <div className="patient-survey__progress-track" role="progressbar" aria-valuenow={percent}>
        <div className="patient-survey__progress-bar" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function RatingQuestion({ question, register, currentValue }) {
  const [hoverValue, setHoverValue] = useState(0);
  const ratingType = ['stars', 'numbers', 'scale'].includes(question.ratingType)
    ? question.ratingType
    : 'stars';
  const values = useMemo(
    () =>
      Array.from(
        { length: question.maxValue - question.minValue + 1 },
        (_, i) => question.minValue + i
      ),
    [question.minValue, question.maxValue]
  );
  const selectedValue = Number(currentValue) || 0;
  const displayValue = hoverValue || selectedValue;
  const requiredRule = question.required ? `${question.title} is required` : false;
  const lastIndex = values.length - 1;

  return (
    <div className={`survey-rating survey-rating--${ratingType}`}>
      {ratingType === 'stars' ? (
        <div
          className="survey-rating__track"
          role="radiogroup"
          aria-label={question.title || 'Rating'}
          onMouseLeave={() => setHoverValue(0)}
        >
          {values.map((ratingValue, index) => {
            const filled = ratingValue <= displayValue;
            return (
              <div key={ratingValue} className="survey-rating__cell">
                <label
                  className={`survey-rating__star${filled ? ' is-filled' : ''}`}
                  onMouseEnter={() => setHoverValue(ratingValue)}
                >
                  <input
                    type="radio"
                    value={ratingValue}
                    className="visually-hidden"
                    {...register(question.id, { required: requiredRule })}
                  />
                  <i
                    className={`bi ${filled ? 'bi-star-fill' : 'bi-star'}`}
                    aria-hidden="true"
                  />
                  <span className="visually-hidden">{ratingValue}</span>
                </label>
                {index === 0 ? (
                  <span className="survey-rating__end-label">{question.minLabel}</span>
                ) : null}
                {index === lastIndex && lastIndex > 0 ? (
                  <span className="survey-rating__end-label">{question.maxLabel}</span>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : null}

      {ratingType === 'numbers' ? (
        <div
          className="survey-rating__track"
          role="radiogroup"
          aria-label={question.title || 'Rating'}
        >
          {values.map((ratingValue, index) => (
            <div key={ratingValue} className="survey-rating__cell">
              <label
                className={`survey-rating__number${
                  selectedValue === ratingValue ? ' is-active' : ''
                }`}
              >
                <input
                  type="radio"
                  value={ratingValue}
                  className="visually-hidden"
                  {...register(question.id, { required: requiredRule })}
                />
                {ratingValue}
              </label>
              {index === 0 ? (
                <span className="survey-rating__end-label">{question.minLabel}</span>
              ) : null}
              {index === lastIndex && lastIndex > 0 ? (
                <span className="survey-rating__end-label">{question.maxLabel}</span>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      {ratingType === 'scale' ? (
        <div className="survey-rating__control survey-rating__control--scale">
          <div className="survey-rating__scale">
            <div className="survey-rating__scale-value">
              {selectedValue || question.minValue} / {question.maxValue}
            </div>
            <input
              type="range"
              className="form-range"
              min={question.minValue}
              max={question.maxValue}
              step={1}
              defaultValue={question.minValue}
              {...register(question.id, {
                required: requiredRule,
                valueAsNumber: true,
              })}
            />
          </div>
          <div className="survey-rating__labels">
            <span>{question.minLabel}</span>
            <span>{question.maxLabel}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SurveyShell({ survey, children, className = '' }) {
  const showCard = Boolean(survey?.formContainerShowCard);
  const containerClass = [
    'patient-survey__container',
    showCard ? 'patient-survey__container--card' : '',
    survey?.formContainerCardShadow ? 'patient-survey__container--shadow' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className="welcome-stack patient-survey-root" style={buildPageStyle(survey)}>
      <section className={containerClass} style={buildContainerStyle(survey)}>
        {children}
      </section>
    </div>
  );
}

function SurveyPage() {
  const { clinicId, surveyId, patientId } = useParams();
  const startedAtRef = useRef(Date.now());

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [survey, setSurvey] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [patientFirstName, setPatientFirstName] = useState('');
  const [completion, setCompletion] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [countdown, setCountdown] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (!clinicId || !surveyId || !patientId) {
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setSubmitError('');
    startedAtRef.current = Date.now();

    clinicService
      .getPatientSurvey(clinicId, surveyId, patientId)
      .then((response) => {
        if (cancelled) {
          return;
        }

        const data = response.data || {};
        setSurvey(data.survey || null);
        setQuestions(normalizeQuestions(data.questions || []));
        setPatientFirstName(data.patientFirstName || '');
        setAlreadySubmitted(Boolean(data.alreadySubmitted));
        setCurrentPage(0);
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Failed to load patient survey', error);
        if (!cancelled) {
          setNotFound(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [clinicId, surveyId, patientId]);

  useEffect(() => {
    const css = survey?.customCss?.trim();
    if (!css) {
      return undefined;
    }

    const styleEl = document.createElement('style');
    styleEl.setAttribute('data-patient-survey-css', survey.id || 'custom');
    styleEl.textContent = css;
    document.head.appendChild(styleEl);

    return () => {
      styleEl.remove();
    };
  }, [survey?.customCss, survey?.id]);

  useEffect(() => {
    if (!completion) {
      return undefined;
    }

    const redirectUrl = normalizeUrl(completion.redirectUrl);
    const redirectSeconds = Number(completion.redirectTimeAfterMessage);
    if (!redirectUrl || !Number.isFinite(redirectSeconds) || redirectSeconds <= 0) {
      return undefined;
    }

    setCountdown(redirectSeconds);
    const tick = setInterval(() => {
      setCountdown((prev) => (prev == null ? prev : Math.max(prev - 1, 0)));
    }, 1000);
    const redirectTimer = setTimeout(() => {
      window.location.href = redirectUrl;
    }, redirectSeconds * 1000);

    return () => {
      clearInterval(tick);
      clearTimeout(redirectTimer);
    };
  }, [completion]);

  const formValues = watch();
  const singleQuestionPerPage = Boolean(
    survey?.singleQuestionPerPage === true || survey?.singleQuestionPerPage === 'true'
  );

  const answeredCount = useMemo(() => {
    return questions.reduce((count, question) => {
      const value = formValues[question.id];
      if (value === undefined || value === null || value === '') {
        return count;
      }
      if (Array.isArray(value) && value.length === 0) {
        return count;
      }
      return count + 1;
    }, 0);
  }, [questions, formValues]);

  const visibleQuestions = singleQuestionPerPage
    ? questions.slice(currentPage, currentPage + 1)
    : questions;
  const isLastPage = !singleQuestionPerPage || currentPage >= questions.length - 1;

  const handleNext = async () => {
    const currentQuestion = questions[currentPage];
    if (!currentQuestion) {
      return;
    }
    const valid = await trigger(currentQuestion.id);
    if (valid) {
      setCurrentPage((prev) => Math.min(prev + 1, questions.length - 1));
    }
  };

  const handlePrevious = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 0));
  };

  const onSubmit = async (formData) => {
    if (singleQuestionPerPage && !isLastPage) {
      await handleNext();
      return;
    }

    setSubmitting(true);
    setSubmitError('');

    const answers = questions
      .map((question) => ({
        questionId: question.id,
        value: serializeAnswerValue(formData[question.id]),
      }))
      .filter((answer) => answer.value !== '' && answer.value !== '[]');

    const durationSeconds = Math.max(
      1,
      Math.round((Date.now() - startedAtRef.current) / 1000)
    );

    try {
      const response = await clinicService.submitPatientSurvey(clinicId, surveyId, patientId, {
        answers,
        durationSeconds,
        channel: 'LINK',
      });
      setCompletion({
        messageAfterComplete:
          response.data?.messageAfterComplete ||
          survey?.messageAfterComplete ||
          DEFAULT_THANK_YOU,
        redirectUrl: response.data?.redirectUrl || survey?.redirectUrl,
        redirectTimeAfterMessage:
          response.data?.redirectTimeAfterMessage ?? survey?.redirectTimeAfterMessage,
      });
      setAlreadySubmitted(true);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to submit patient survey', error);
      const status = error?.response?.status;
      if (status === 409) {
        setAlreadySubmitted(true);
        setSubmitError('This survey has already been submitted.');
      } else {
        setSubmitError(
          error?.response?.data?.message ||
            'Unable to submit your survey. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="welcome-stack patient-survey-root">
        <section className="patient-survey__container patient-survey__container--card patient-survey__state">
          <div className="rep-spinner" />
        </section>
      </div>
    );
  }

  if (notFound || !survey) {
    return (
      <div className="welcome-stack patient-survey-root">
        <section className="patient-survey__container patient-survey__container--card">
          <h2 className="patient-survey__title">Survey not available</h2>
          <p className="patient-survey__description mb-0">
            This survey link is invalid or the survey is no longer available.
          </p>
        </section>
      </div>
    );
  }

  if (completion) {
    const redirectUrl = normalizeUrl(completion.redirectUrl);
    const messageHtml = completion.messageAfterComplete || DEFAULT_THANK_YOU;

    return (
      <SurveyShell survey={survey} className="patient-survey__container--complete">
        <div className="patient-survey__complete">
          <span className="patient-survey__complete-icon" aria-hidden="true">
            <i className="bi bi-check2-circle" />
          </span>
          <h2 className="patient-survey__title text-center">
            Thank you{patientFirstName ? `, ${patientFirstName}` : ''}
          </h2>
          <div
            className="patient-survey__complete-message"
            dangerouslySetInnerHTML={{ __html: messageHtml }}
          />
          {countdown != null && countdown > 0 && redirectUrl && (
            <p className="patient-survey__redirect-note">
              Redirecting in {countdown} second{countdown === 1 ? '' : 's'}…
            </p>
          )}
        </div>
      </SurveyShell>
    );
  }

  if (alreadySubmitted) {
    return (
      <SurveyShell survey={survey}>
        <h2 className="patient-survey__title">Already submitted</h2>
        <p className="patient-survey__description mb-0">
          {submitError ||
            'You have already completed this survey. Thank you for your feedback.'}
        </p>
      </SurveyShell>
    );
  }

  return (
    <SurveyShell survey={survey}>
      <SurveyHeader survey={survey} patientFirstName={patientFirstName} />
      <SurveyProgress
        survey={survey}
        answeredCount={answeredCount}
        total={questions.length}
        currentPage={currentPage}
        singleQuestionPerPage={singleQuestionPerPage}
      />

      <form className="survey-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {visibleQuestions.map((question, visibleIndex) => {
          const index = singleQuestionPerPage ? currentPage : visibleIndex;
          return (
          <div key={question.id || question.internalId} className="survey-question">
            <label className="survey-question__title">
              <span className="survey-question__index">{index + 1}.</span>
              {question.title}
              {question.required ? <span className="survey-required">*</span> : null}
            </label>
            {question.description && (
              <p className="survey-question__description">{question.description}</p>
            )}

            {question.type === 'text' && (
              <input
                type="text"
                className={`form-control ${errors[question.id] ? 'is-invalid' : ''}`}
                placeholder={question.placeholder || ''}
                maxLength={question.maxLength || undefined}
                {...register(question.id, {
                  required: question.required ? `${question.title} is required` : false,
                })}
              />
            )}

            {question.type === 'textarea' && (
              <textarea
                className={`form-control ${errors[question.id] ? 'is-invalid' : ''}`}
                rows={question.rows || 4}
                placeholder={question.placeholder || ''}
                maxLength={question.maxLength || undefined}
                {...register(question.id, {
                  required: question.required ? `${question.title} is required` : false,
                })}
              />
            )}

            {question.type === 'dropdown' && (
              <select
                className={`form-select ${errors[question.id] ? 'is-invalid' : ''}`}
                {...register(question.id, {
                  required: question.required ? `${question.title} is required` : false,
                })}
              >
                <option value="">{question.placeholder || 'Select an option'}</option>
                {question.options.map((opt) => (
                  <option key={opt.id} value={opt.value}>
                    {opt.text}
                  </option>
                ))}
              </select>
            )}

            {question.type === 'single_choice' && (
              <div className="survey-options">
                {question.options.map((opt) => (
                  <label key={opt.id} className="survey-option">
                    <input
                      className="form-check-input"
                      type="radio"
                      value={opt.value}
                      {...register(question.id, {
                        required: question.required ? `${question.title} is required` : false,
                      })}
                    />
                    <span>{opt.text}</span>
                  </label>
                ))}
                {question.options.length === 0 && (
                  <p className="survey-unsupported mb-0">No choices are configured for this question.</p>
                )}
              </div>
            )}

            {question.type === 'multiple_choice' && (
              <div className="survey-options">
                {question.options.map((opt) => (
                  <label key={opt.id} className="survey-option">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      value={opt.value}
                      {...register(question.id, {
                        validate: (value) =>
                          !question.required ||
                          (Array.isArray(value) && value.length > 0) ||
                          `${question.title} is required`,
                      })}
                    />
                    <span>{opt.text}</span>
                  </label>
                ))}
                {question.options.length === 0 && (
                  <p className="survey-unsupported mb-0">No choices are configured for this question.</p>
                )}
              </div>
            )}

            {question.type === 'rating' && (
              <RatingQuestion
                question={question}
                register={register}
                currentValue={formValues[question.id]}
              />
            )}

            {!['text', 'textarea', 'dropdown', 'single_choice', 'multiple_choice', 'rating'].includes(
              question.type
            ) && (
              <p className="survey-unsupported">
                This question type is not supported in the patient survey form.
              </p>
            )}

            {errors[question.id] && (
              <div className="invalid-feedback d-block">{errors[question.id].message}</div>
            )}
          </div>
          );
        })}

        {submitError && <div className="alert alert-danger">{submitError}</div>}

        <div className={`survey-actions${singleQuestionPerPage ? ' survey-actions--paged' : ''}`}>
          {singleQuestionPerPage && currentPage > 0 ? (
            <button
              type="button"
              className="btn btn-outline-secondary rounded-pill px-4"
              onClick={handlePrevious}
              disabled={submitting}
            >
              Previous
            </button>
          ) : (
            <span />
          )}

          {singleQuestionPerPage && !isLastPage ? (
            <button
              type="button"
              className="btn btn-primary rounded-pill px-4"
              onClick={handleNext}
              disabled={submitting}
            >
              Next
            </button>
          ) : (
            <button type="submit" className="btn btn-primary rounded-pill px-4" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit survey'}
            </button>
          )}
        </div>
      </form>
    </SurveyShell>
  );
}

export default SurveyPage;
